import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { StatusHistoryItem } from '../../../utils/types/JobOpenings';

export interface StatusHistoryTimelineProps {
  statusHistory?: StatusHistoryItem[];
  currentStatus?: string;
  createdAt?: number | string;
  className?: string;
  title?: string;
  collapsible?: boolean;
}

// Helpers for timestamp parsing and formatting
const parseTimestampToDate = (ts: number | string | undefined | null): Date | null => {
  if (!ts) return null;
  if (typeof ts === 'number') {
    // If Unix timestamp in seconds (10 digits), convert to ms
    return new Date(ts < 100000000000 ? ts * 1000 : ts);
  }
  const num = Number(ts);
  if (!isNaN(num)) {
    return new Date(num < 100000000000 ? num * 1000 : num);
  }
  const parsed = new Date(ts);
  return isNaN(parsed.getTime()) ? null : parsed;
};

const formatFullDate = (date: Date | null): string => {
  if (!date) return 'Date unknown';
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

const formatRelativeTime = (date: Date | null): string => {
  if (!date) return '';
  const now = Date.now();
  const diffSec = Math.floor((now - date.getTime()) / 1000);

  if (diffSec < 0) return 'Just now';
  if (diffSec < 60) return 'Just now';
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  if (diffSec < 604800) return `${Math.floor(diffSec / 86400)}d ago`;
  if (diffSec < 2592000) return `${Math.floor(diffSec / 604800)}w ago`;
  return `${Math.floor(diffSec / 2592000)}mo ago`;
};

// Configuration for status colors, icons, and themes
interface StatusConfig {
  label: string;
  bgClass: string;
  borderClass: string;
  textClass: string;
  dotBg: string;
  ringColor: string;
  icon: React.ReactNode;
}

const getStatusConfig = (statusRaw: string): StatusConfig => {
  const status = (statusRaw || '').trim();

  switch (status) {
    case 'New':
      return {
        label: 'New Application',
        bgClass: 'bg-amber-50/80',
        borderClass: 'border-amber-200',
        textClass: 'text-amber-800',
        dotBg: 'bg-amber-500',
        ringColor: 'rgba(245, 158, 11, 0.4)',
        icon: (
          <svg className='w-3.5 h-3.5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 4v16m8-8H4' />
          </svg>
        ),
      };
    case 'Suitable':
      return {
        label: 'Suitable',
        bgClass: 'bg-teal-50/80',
        borderClass: 'border-teal-200',
        textClass: 'text-teal-800',
        dotBg: 'bg-teal-500',
        ringColor: 'rgba(20, 184, 166, 0.4)',
        icon: (
          <svg className='w-3.5 h-3.5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M5 13l4 4L19 7' />
          </svg>
        ),
      };
    case 'Outstanding':
      return {
        label: 'Outstanding',
        bgClass: 'bg-blue-50/80',
        borderClass: 'border-blue-200',
        textClass: 'text-blue-800',
        dotBg: 'bg-blue-500',
        ringColor: 'rgba(59, 130, 246, 0.4)',
        icon: (
          <svg className='w-3.5 h-3.5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z' />
          </svg>
        ),
      };
    case 'Auto-Assessment':
      return {
        label: 'Auto-Assessment',
        bgClass: 'bg-purple-50/80',
        borderClass: 'border-purple-200',
        textClass: 'text-purple-800',
        dotBg: 'bg-purple-500',
        ringColor: 'rgba(168, 85, 247, 0.4)',
        icon: (
          <svg className='w-3.5 h-3.5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4' />
          </svg>
        ),
      };
    case 'Shortlisted':
      return {
        label: 'Shortlisted',
        bgClass: 'bg-emerald-50/80',
        borderClass: 'border-emerald-200',
        textClass: 'text-emerald-800',
        dotBg: 'bg-emerald-500',
        ringColor: 'rgba(16, 185, 129, 0.4)',
        icon: (
          <svg className='w-3.5 h-3.5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' />
          </svg>
        ),
      };
    case 'Interview':
    case 'Scheduled':
      return {
        label: 'Interview',
        bgClass: 'bg-indigo-50/80',
        borderClass: 'border-indigo-200',
        textClass: 'text-indigo-800',
        dotBg: 'bg-indigo-500',
        ringColor: 'rgba(99, 102, 241, 0.4)',
        icon: (
          <svg className='w-3.5 h-3.5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z' />
          </svg>
        ),
      };
    case 'Offer':
      return {
        label: 'Job Offer',
        bgClass: 'bg-cyan-50/80',
        borderClass: 'border-cyan-200',
        textClass: 'text-cyan-800',
        dotBg: 'bg-cyan-500',
        ringColor: 'rgba(6, 182, 212, 0.4)',
        icon: (
          <svg className='w-3.5 h-3.5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z' />
          </svg>
        ),
      };
    case 'Completed':
      return {
        label: 'Completed',
        bgClass: 'bg-green-50/80',
        borderClass: 'border-green-200',
        textClass: 'text-green-800',
        dotBg: 'bg-green-500',
        ringColor: 'rgba(34, 197, 94, 0.4)',
        icon: (
          <svg className='w-3.5 h-3.5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M5 13l4 4L19 7' />
          </svg>
        ),
      };
    case 'Unsuccessful':
    case 'Unsuccessful-Closed':
    case 'Unsuccessful-Pending':
      return {
        label: 'Unsuccessful',
        bgClass: 'bg-rose-50/80',
        borderClass: 'border-rose-200',
        textClass: 'text-rose-800',
        dotBg: 'bg-rose-500',
        ringColor: 'rgba(244, 63, 94, 0.4)',
        icon: (
          <svg className='w-3.5 h-3.5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M6 18L18 6M6 6l12 12' />
          </svg>
        ),
      };
    case 'Waitlisted':
      return {
        label: 'Waitlisted',
        bgClass: 'bg-slate-100/90',
        borderClass: 'border-slate-300',
        textClass: 'text-slate-800',
        dotBg: 'bg-slate-500',
        ringColor: 'rgba(100, 116, 139, 0.4)',
        icon: (
          <svg className='w-3.5 h-3.5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z' />
          </svg>
        ),
      };
    default:
      return {
        label: status || 'Updated',
        bgClass: 'bg-slate-50/80',
        borderClass: 'border-slate-200',
        textClass: 'text-slate-800',
        dotBg: 'bg-slate-500',
        ringColor: 'rgba(148, 163, 184, 0.4)',
        icon: (
          <svg className='w-3.5 h-3.5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z' />
          </svg>
        ),
      };
  }
};

// Motion animation variants for container and child items
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.05,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 12, scale: 0.98 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      type: 'spring',
      stiffness: 400,
      damping: 28,
    },
  },
};

export const StatusHistoryTimeline: React.FC<StatusHistoryTimelineProps> = ({
  statusHistory = [],
  currentStatus,
  createdAt,
  className = '',
  title = 'Application Timeline',
  collapsible = false,
}) => {
  const [isNewestFirst, setIsNewestFirst] = useState<boolean>(true);
  const [isExpanded, setIsExpanded] = useState<boolean>(true);

  // Normalize history list
  const historyItems: StatusHistoryItem[] = React.useMemo(() => {
    let items = Array.isArray(statusHistory) ? [...statusHistory] : [];

    // If history is empty but currentStatus is known, synthesize an entry
    if (items.length === 0 && currentStatus && currentStatus !== 'N/A') {
      items.push({
        status: currentStatus,
        timestamp: createdAt || Date.now(),
        actorId: null,
      });
    }

    // Sort items by parsed timestamp
    items.sort((a, b) => {
      const dateA = parseTimestampToDate(a.timestamp)?.getTime() || 0;
      const dateB = parseTimestampToDate(b.timestamp)?.getTime() || 0;
      return isNewestFirst ? dateB - dateA : dateA - dateB;
    });

    return items;
  }, [statusHistory, currentStatus, createdAt, isNewestFirst]);

  if (historyItems.length === 0 && (!currentStatus || currentStatus === 'N/A')) {
    return null;
  }

  return (
    <section
      className={`bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden ${className}`}
    >
      {/* Header bar */}
      <div className='bg-gradient-to-r from-emerald-50 via-teal-50/50 to-white px-4 py-3 border-b border-gray-100 flex items-center justify-between'>
        <div className='flex items-center gap-2'>
          <div className='w-7 h-7 rounded-lg bg-emerald-600/10 text-emerald-700 flex items-center justify-center font-bold'>
            <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z'
              />
            </svg>
          </div>
          <div>
            <h3 className='text-sm font-bold text-gray-900 flex items-center gap-1.5'>
              {title}
              <span className='text-[10px] font-semibold text-emerald-700 bg-emerald-100/80 px-2 py-0.5 rounded-full'>
                {historyItems.length} {historyItems.length === 1 ? 'stage' : 'stages'}
              </span>
            </h3>
            {currentStatus && (
              <p className='text-[11px] text-gray-500 font-medium'>
                Current Stage:{' '}
                <span className='font-bold text-emerald-700'>{currentStatus}</span>
              </p>
            )}
          </div>
        </div>

        {/* Action Controls: Sort toggle + Optional Collapse */}
        <div className='flex items-center gap-1.5'>
          {historyItems.length > 1 && (
            <button
              type='button'
              onClick={() => setIsNewestFirst(!isNewestFirst)}
              className='px-2.5 py-1 text-[11px] font-semibold rounded-lg bg-white border border-gray-200 text-gray-600 hover:text-emerald-700 hover:border-emerald-300 transition-all flex items-center gap-1 shadow-xs'
              title={isNewestFirst ? 'Showing newest first' : 'Showing oldest first'}
            >
              <svg className='w-3 h-3 text-gray-400' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4' />
              </svg>
              {isNewestFirst ? 'Newest' : 'Chronological'}
            </button>
          )}

          {collapsible && (
            <button
              type='button'
              onClick={() => setIsExpanded(!isExpanded)}
              className='p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition'
              aria-label={isExpanded ? 'Collapse timeline' : 'Expand timeline'}
            >
              <svg
                className={`w-4 h-4 transform transition-transform duration-200 ${
                  isExpanded ? 'rotate-180' : ''
                }`}
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 9l-7 7-7-7' />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Animated Timeline List */}
      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: 'easeInOut' }}
            className='overflow-hidden'
          >
            <motion.div
              variants={containerVariants}
              initial='hidden'
              animate='visible'
              className='p-4 sm:p-5 relative'
            >
              {/* Vertical connecting rail */}
              <div
                className='absolute left-8 sm:left-9 top-7 bottom-7 w-0.5 bg-gradient-to-b from-emerald-500 via-teal-400 to-slate-200'
                style={{ zIndex: 0 }}
              />

              <div className='space-y-4 relative' style={{ zIndex: 1 }}>
                {historyItems.map((item, index) => {
                  const dateObj = parseTimestampToDate(item.timestamp);
                  const isLatest = index === 0;
                  const config = getStatusConfig(item.status);

                  return (
                    <motion.div
                      key={`${item.status}-${item.timestamp}-${index}`}
                      variants={itemVariants}
                      whileHover={{ x: 2, transition: { duration: 0.15 } }}
                      className='flex items-start gap-3.5 group'
                    >
                      {/* Node Icon on Timeline */}
                      <div className='relative flex items-center justify-center shrink-0 mt-0.5'>
                        {isLatest && (
                          <motion.span
                            className='absolute -inset-1 rounded-full'
                            style={{ backgroundColor: config.ringColor }}
                            animate={{
                              scale: [1, 1.45, 1],
                              opacity: [0.7, 0, 0.7],
                            }}
                            transition={{
                              duration: 2.2,
                              repeat: Infinity,
                              ease: 'easeInOut',
                            }}
                          />
                        )}
                        <div
                          className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-white ${config.dotBg} shadow-sm border-2 border-white`}
                        >
                          {config.icon}
                        </div>
                      </div>

                      {/* Notification / Event Card */}
                      <div
                        className={`flex-1 rounded-xl p-3 sm:p-3.5 border transition-all duration-200 ${config.bgClass} ${config.borderClass} shadow-xs hover:shadow-sm`}
                      >
                        <div className='flex flex-wrap items-center justify-between gap-1.5'>
                          {/* Status Badge & Label */}
                          <div className='flex items-center gap-2'>
                            <span
                              className={`text-xs font-bold uppercase tracking-wide px-2 py-0.5 rounded-md border ${config.borderClass} ${config.textClass} bg-white/80`}
                            >
                              {item.status}
                            </span>
                            {isLatest && (
                              <span className='inline-flex items-center gap-1 text-[10px] font-extrabold text-emerald-700 bg-emerald-100/90 px-2 py-0.5 rounded-full uppercase tracking-wider'>
                                <span className='w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse' />
                                Current
                              </span>
                            )}
                          </div>

                          {/* Relative & Full Time Pill */}
                          {dateObj && (
                            <div className='flex items-center gap-1.5 text-[11px] text-gray-500 font-medium'>
                              <span className='hidden sm:inline text-gray-400'>•</span>
                              <span>{formatFullDate(dateObj)}</span>
                              <span className='bg-white/70 border border-gray-200/60 px-1.5 py-0.5 rounded text-[10px] font-semibold text-gray-600'>
                                {formatRelativeTime(dateObj)}
                              </span>
                            </div>
                          )}
                        </div>

                        {/* Actor / Trigger source */}
                        <div className='mt-1.5 flex flex-wrap items-center gap-2 text-xs text-gray-600'>
                          <span className='text-[11px] font-medium inline-flex items-center gap-1 text-gray-500'>
                            <svg className='w-3 h-3 text-gray-400' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z' />
                            </svg>
                            {item.actorName
                              ? `Action by ${item.actorName}`
                              : item.actorId
                              ? `Actor: ${item.actorId}`
                              : 'System Pipeline Automation'}
                          </span>
                        </div>

                        {/* Note / Reason if present */}
                        {(item.note || item.reason) && (
                          <div className='mt-2 text-xs bg-white/90 rounded-lg p-2 border border-gray-100/80 text-gray-700 font-normal leading-relaxed'>
                            <p className='italic'>{item.note || item.reason}</p>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default StatusHistoryTimeline;
